import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePickerModal } from '../../../components/common/DatePickerModal';
import api from '../../../services/api';
import { Medicine } from '../../../services/medicineService';
import styles from '../ReportMobile.module.css';

const StockInOutReportMobile: React.FC = () => {
  const [dateRange, setDateRange] = useState<[
    string,
    string
  ]>([
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD')
  ]);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [selectedMedicine, setSelectedMedicine] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState('');

  // Date picker modal states
  const [startDateModalOpen, setStartDateModalOpen] = useState(false);
  const [endDateModalOpen, setEndDateModalOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(dayjs().startOf('month'));
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(dayjs().endOf('month'));

  useEffect(() => {
    api.get('/api/medicines').then(res => {
      setMedicines(res.data.data || []);
    });
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: any = {
        start_date: dateRange[0],
        end_date: dateRange[1],
      };
      if (selectedMedicine) params.medicine_id = selectedMedicine;
      const res = await api.get('/api/reports/nxt', { params });
      setData(res.data.data || []);
    } catch (err) {
      alert('Không thể lấy dữ liệu báo cáo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, [dateRange, selectedMedicine]);

  // Handle date selection
  const handleStartDateSelect = (date: Dayjs) => {
    setSelectedStartDate(date);
    setDateRange([date.format('YYYY-MM-DD'), dateRange[1]]);
  };

  const handleEndDateSelect = (date: Dayjs) => {
    setSelectedEndDate(date);
    setDateRange([dateRange[0], date.format('YYYY-MM-DD')]);
  };

  // Tổng hợp
  const sum = (key: string) => data.reduce((acc: number, cur: any) => acc + (cur[key] || 0), 0);

  return (
    <div className={styles.bgGray}>
      <div className={styles.stickyHeader}>
        <div className={styles.title}>Báo cáo nhập - xuất - tồn kho</div>
        <div className={styles.flexRow}>
          <button
            onClick={() => setStartDateModalOpen(true)}
            className={styles.inputDate}
            style={{ 
              background: '#fff', 
              border: '1px solid #ced4da', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedStartDate.format('DD/MM/YYYY')}
          </button>
          <span className={styles.textGray} style={{ alignSelf: 'center' }}>-</span>
          <button
            onClick={() => setEndDateModalOpen(true)}
            className={styles.inputDate}
            style={{ 
              background: '#fff', 
              border: '1px solid #ced4da', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedEndDate.format('DD/MM/YYYY')}
          </button>
        </div>
        <select
          value={selectedMedicine}
          onChange={e => setSelectedMedicine(e.target.value)}
          className={styles.select}
        >
          <option value="">Tất cả thuốc</option>
          {medicines.map(med => (
            <option key={med.id} value={med.id}>{med.name} ({med.code})</option>
          ))}
        </select>
        <button
          onClick={fetchReport}
          disabled={loading}
          className={styles.button}
        >
          {loading ? 'Đang tải...' : 'Xem báo cáo'}
        </button>
      </div>
      <div style={{ padding: 8 }}>
        {/* Ô tìm kiếm */}
        <input
          type="text"
          placeholder="Tìm kiếm tên thuốc..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={styles.inputSearch}
        />
        <div className={styles.tableWrapper + ' ' + styles.section} style={{ marginTop: 8 }}>
          <table className={styles.table} style={{ minWidth: 600 }}>
            <thead>
              <tr>
                <th>Tên thuốc</th>
                <th>Đơn vị</th>
                <th className={styles.tableCellRight}>Tồn đầu</th>
                <th className={styles.tableCellRight}>Nhập</th>
                <th className={styles.tableCellRight}>Xuất</th>
                <th className={styles.tableCellRight}>Tồn cuối</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className={styles.textGray} style={{ textAlign: 'center', padding: 24 }}>Không có dữ liệu</td>
                </tr>
              )}
              {data
                .filter(row =>
                  row.medicine_name
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/\p{Diacritic}/gu, '')
                    .includes(
                      search
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/\p{Diacritic}/gu, '')
                    )
                )
                .map(row => (
                  <tr key={row.medicine_id}>
                    <td className={styles.tableCellEllipsis}>{row.medicine_name}</td>
                    <td className={styles.tableCellCenter}>{row.unit}</td>
                    <td className={styles.tableCellRight}>{row.ton_dau_ky?.toLocaleString('vi-VN')}</td>
                    <td className={styles.tableCellRight}>{row.nhap_trong_ky?.toLocaleString('vi-VN')}</td>
                    <td className={styles.tableCellRight}>{row.xuat_trong_ky?.toLocaleString('vi-VN')}</td>
                    <td className={styles.tableCellRight}>{row.ton_cuoi_ky?.toLocaleString('vi-VN')}</td>
                  </tr>
                ))}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr className={styles.tableRowTotal}>
                  <td>Tổng</td>
                  <td />
                  <td className={styles.tableCellRight}>{sum('ton_dau_ky').toLocaleString('vi-VN')}</td>
                  <td className={styles.tableCellRight}>{sum('nhap_trong_ky').toLocaleString('vi-VN')}</td>
                  <td className={styles.tableCellRight}>{sum('xuat_trong_ky').toLocaleString('vi-VN')}</td>
                  <td className={styles.tableCellRight}>{sum('ton_cuoi_ky').toLocaleString('vi-VN')}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Date Picker Modals */}
      <DatePickerModal
        isOpen={startDateModalOpen}
        onClose={() => setStartDateModalOpen(false)}
        onSelect={handleStartDateSelect}
        selectedDate={selectedStartDate}
        title="Chọn ngày bắt đầu"
      />
      <DatePickerModal
        isOpen={endDateModalOpen}
        onClose={() => setEndDateModalOpen(false)}
        onSelect={handleEndDateSelect}
        selectedDate={selectedEndDate}
        title="Chọn ngày kết thúc"
      />
    </div>
  );
};

export default StockInOutReportMobile;